import React from 'react';
import {
  GlobeAltIcon,
  ExclamationTriangleIcon,
  UserGroupIcon,
  MapPinIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  CpuChipIcon
} from '@heroicons/react/24/outline';

/**
 * AnalyticsOverview - Summary stats cards for nginx traffic
 */
const AnalyticsOverview = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-20 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-16"></div>
          </div>
        ))}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const cards = [
    {
      label: 'Total Requests',
      value: data.totalRequests?.toLocaleString() || '0',
      icon: GlobeAltIcon,
      color: 'blue',
      subtext: `${data.requestsPerMinute || 0}/min`
    },
    {
      label: 'Error Rate',
      value: `${data.errorRate || 0}%`,
      icon: ExclamationTriangleIcon,
      color: data.errorRate > 5 ? 'red' : data.errorRate > 2 ? 'yellow' : 'green',
      subtext: `${data.errorCount || 0} errors`
    },
    {
      label: 'Unique IPs',
      value: data.uniqueIPs?.toLocaleString() || '0',
      icon: UserGroupIcon,
      color: 'purple'
    },
    {
      label: 'Countries',
      value: data.countries || '0',
      icon: MapPinIcon,
      color: 'indigo'
    },
    {
      label: 'Avg Response',
      value: `${((data.avgResponseTime || 0) * 1000).toFixed(0)}ms`,
      icon: ClockIcon,
      color: data.avgResponseTime > 1 ? 'red' : data.avgResponseTime > 0.5 ? 'yellow' : 'green'
    },
    {
      label: 'Bot Traffic',
      value: `${data.botPercentage || 0}%`,
      icon: CpuChipIcon,
      color: 'gray',
      subtext: `${data.botCount || 0} bots`
    },
    {
      label: 'Success Rate',
      value: `${(100 - (data.errorRate || 0)).toFixed(1)}%`,
      icon: ArrowTrendingUpIcon,
      color: 'green'
    }
  ];

  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    red: 'bg-red-50 text-red-600 border-red-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    gray: 'bg-gray-50 text-gray-600 border-gray-200'
  };

  const iconColorClasses = {
    blue: 'text-blue-500',
    red: 'text-red-500',
    green: 'text-green-500',
    yellow: 'text-yellow-500',
    purple: 'text-purple-500',
    indigo: 'text-indigo-500',
    gray: 'text-gray-500'
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
      {cards.map((card, index) => (
        <div
          key={index}
          className={`rounded-lg border p-4 ${colorClasses[card.color]}`}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium uppercase tracking-wide opacity-75">
              {card.label}
            </span>
            <card.icon className={`w-4 h-4 ${iconColorClasses[card.color]}`} />
          </div>
          <div className="text-2xl font-bold">{card.value}</div>
          {card.subtext && (
            <div className="text-xs opacity-75 mt-1">{card.subtext}</div>
          )}
        </div>
      ))}
    </div>
  );
};

export default AnalyticsOverview;
